import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ChartWrapperInstance, ChartWrapperService } from '../../shared-services/chart-wrapper.service';
import { ConfigService } from '../../shared-services/config.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';

type SeriesMode = 'bar' | 'line';

interface DivisionSegment {
  label: string;
  valueKey: string;
  startIndex: number;
  endIndex: number;
  gridColumn: string;
  compact: boolean;
}

interface DivisionRow {
  label: string;
  valueKey: string;
  segments: DivisionSegment[];
}

interface ViewTab {
  id: string;
  label: string;
  data?: any;
  apiUrl?: string;
  method?: string;
  title?: string;
  subtitle?: string;
  chartConfig?: any;
}

interface NormalizedSeriesConfig {
  mode: SeriesMode;
  name: string;
  valueField: string;
  color?: string;
  yAxisIndex: number;
  barWidth?: number | string;
  lineWidth?: number;
  smooth?: boolean;
  connectNulls?: boolean;
  symbol?: string;
  symbolSize?: number;
  showLabel?: boolean;
  labelRotate?: number;
  valuePrefix?: string;
  valueSuffix?: string;
  valueDecimals?: number;
}

@Component({
  selector: 'app-named-bar-chart-with-division',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './named-bar-chart-with-division.component.html',
  styleUrl: './named-bar-chart-with-division.component.scss',
})
export class NamedBarChartWithDivisionComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data!: any;
  @ViewChild('cardRoot', { static: false }) private cardRoot?: ElementRef<HTMLDivElement>;
  @ViewChild('plotScroll', { static: false }) private plotScroll?: ElementRef<HTMLDivElement>;
  @ViewChild('chartHost', { static: false }) private chartHost?: ElementRef<HTMLDivElement>;

  activeViewIndex = 0;
  currentTitle = 'Customer View';
  currentSubtitle = '';
  categoryCount = 0;
  divisionRows: DivisionRow[] = [];
  divisionGridTemplate = '';
  viewTabs: ViewTab[] = [];
  divisionLabelWidth = 96;
  gridRightWidth = 28;
  plotMinWidth = 0;
  categoryAreaWidth = 0;
  fitToWidth = false;
  protected allCategories: string[] = [];
  protected allSeriesNames: string[] = [];
  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();

  private myChart?: echarts.ECharts;
  private activeConfig: any = {};
  private lastRows: any[] = [];
  private lastRawRows: any[] = [];
  private dataSub?: Subscription;
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private resizeObserver?: ResizeObserver;
  private resizeFrame = 0;
  private resizeHandler = () => this.scheduleResize();
  private configService = inject(ConfigService);
  private chartFilterHandler = inject(ChartFilterHandlerService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private selectedDivisionKey = '';

  ngAfterViewInit(): void {
    this.refreshViewState();
    this.initChart();
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart || null,
      getSeriesNames: () => this.allSeriesNames,
      getCategories: () => this.allCategories,
      onHighlightCategories: (categories) => this.highlightCategories(categories),
      onResetCategoryHighlight: () => this.resetCategoryHighlight(),
      onSelectLegendsByIndices: (indices) => this.selectLegendsByIndices(indices),
    });
    this.loadAndRenderChart(initialFilters ?? undefined);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => this.scheduleResize());
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.chartHost?.nativeElement) {
        return;
      }
      this.myChart?.dispose();
      this.myChart = echarts.init(this.chartHost.nativeElement, 'dvm');
      this.renderChart(this.lastRawRows);
    });

    window.addEventListener('resize', this.resizeHandler);
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleResize());
      const observedElements = [
        this.cardRoot?.nativeElement,
        this.plotScroll?.nativeElement,
        this.chartHost?.nativeElement,
      ];
      observedElements.forEach((element) => {
        if (element) {
          this.resizeObserver?.observe(element);
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange && this.myChart) {
      this.activeViewIndex = 0;
      this.refreshViewState();
      this.wrapper.updateData(this.data);
      this.loadAndRenderChart();
    }
  }

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.resizeObserver?.disconnect();
    if (this.resizeFrame) {
      cancelAnimationFrame(this.resizeFrame);
    }
    this.dataSub?.unsubscribe();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    window.removeEventListener('resize', this.resizeHandler);
    this.myChart?.dispose();
  }

  selectView(index: number): void {
    if (index === this.activeViewIndex) {
      return;
    }
    this.activeViewIndex = index;
    this.refreshViewState();
    this.loadAndRenderChart();
  }

  private initChart(): void {
    if (!this.chartHost?.nativeElement || this.myChart) {
      return;
    }

    this.myChart = echarts.init(this.chartHost.nativeElement, 'dvm');
  }

  private loadAndRenderChart(filters?: any): void {
    this.initChart();
    this.dataSub?.unsubscribe();
    this.currentTitle = String(this.activeView().title || this.data?.title || 'Customer View');
    this.currentSubtitle = String(this.activeView().subtitle || this.data?.subtitle || '');

    const inlineData = this.activeView().data ?? this.data?.data;
    if (inlineData) {
      this.renderChart(this.extractRows(inlineData));
      return;
    }

    const apiUrl = this.activeView().apiUrl || this.data?.apiUrl;
    if (!apiUrl) {
      this.renderChart([]);
      return;
    }

    this.myChart?.showLoading();
    this.dataSub = this.configService
      .getConfig(apiUrl, this.activeWidgetData(), filters, this.activeView().method || this.data?.method)
      .subscribe({
        next: (response: any) => this.renderChart(this.extractRows(response)),
        error: () => this.renderChart([]),
      });
  }

  private renderChart(rawRows: any[]): void {
    if (!this.myChart) {
      return;
    }

    this.lastRawRows = rawRows;
    const limitedRawRows = this.limitRows(rawRows);
    const rows = this.normalizeRowsForConfiguredSeries(limitedRawRows);
    this.lastRows = rows;
    const config = this.getConfig();
    const categories = this.buildCategories(rows);
    this.categoryCount = categories.length;
    this.allCategories = categories;
    this.divisionLabelWidth = Number(config.divisionLabelWidth ?? config.gridLeft ?? 96);
    this.fitToWidth = config.disableHorizontalScroll === true || config.fitToWidth === true;
    const categorySlotWidth = Number(config.categorySlotWidth ?? config.barSlotWidth ?? 34);
    this.gridRightWidth = Number(config.gridRight ?? 28);
    this.categoryAreaWidth = Math.max(categories.length * categorySlotWidth, 1);
    this.plotMinWidth = this.fitToWidth
      ? 0
      : Math.max(0, this.divisionLabelWidth + this.categoryAreaWidth + this.gridRightWidth);
    this.divisionGridTemplate = this.fitToWidth
      ? `repeat(${Math.max(this.categoryCount, 1)}, minmax(0, 1fr))`
      : `repeat(${Math.max(this.categoryCount, 1)}, ${categorySlotWidth}px)`;
    this.divisionRows = this.buildDivisionRows(rows, categories);

    if (!rows.length || !categories.length) {
      this.showNoData();
      return;
    }

    const seriesConfigs = this.resolveSeriesConfigs(rows);
    this.allSeriesNames = seriesConfigs.map((series) => series.name);
    this.wrapper.setSourceData(
      rows,
      this.categoryKey(),
      this.valueKey(),
      this.getConfig().seriesKey || this.data?.seriesKey || '',
    );
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const option: echarts.EChartsOption = {
      color: seriesConfigs.map((series, index) => series.color || this.palette()[index % this.palette().length]),
      grid: {
        top: Number(config.gridTop ?? 54),
        right: Number(config.gridRight ?? 30),
        bottom: Number(config.gridBottom ?? (config.showXAxisLabels === true ? 28 : 2)),
        left: this.divisionLabelWidth,
        containLabel: config.containLabel === true,
      },
      legend: {
        show: config.showLegend !== false,
        data: seriesConfigs.map((series) => series.name),
        top: Number(config.legendTop ?? 4),
        left: config.legendLeft ?? 'center',
        orient: config.legendOrient || config.legendOrientation || 'horizontal',
        type: config.legendType ?? 'scroll',
        itemWidth: Number(config.legendItemWidth ?? 15),
        itemHeight: Number(config.legendItemHeight ?? 10),
        textStyle: {
          color: theme.chart?.legendTextColor || config.legendTextColor || '#202020',
          fontSize: Number(config.legendFontSize ?? 12),
          fontWeight: config.legendFontWeight ?? 600,
        },
      },
      tooltip: {
        trigger: config.tooltipTrigger || config.tooltip?.trigger || 'axis',
        confine: true,
        axisPointer: { type: config.axisPointerType || 'shadow' },
        backgroundColor: theme.tooltip?.bg || '#ffffff',
        borderColor: theme.tooltip?.borderColor || '#cfcfcf',
        textStyle: {
          color: theme.tooltip?.textColor || '#222222',
          fontSize: theme.tooltip?.fontSize || 12,
        },
        formatter: (params: any) => this.formatTooltip(params),
      },
      xAxis: {
        type: 'category',
        data: categories,
        triggerEvent: true,
        axisTick: { alignWithLabel: true },
        axisLine: { lineStyle: { color: config.axisLineColor || '#858585' } },
        axisLabel: {
          show: config.showXAxisLabels === true,
          color: config.xAxisLabelColor || '#111111',
          fontSize: Number(config.xAxisFontSize ?? 12),
          fontWeight: config.xAxisFontWeight ?? 700,
          rotate: Number(config.xAxisRotate ?? 0),
          interval: config.xAxisInterval ?? 0,
        },
      },
      yAxis: this.buildYAxis(config),
      series: [
        ...seriesConfigs.map((series) => this.buildSeries(series, rows, categories, config)),
        ...this.buildStackTotalSeries(seriesConfigs, rows, categories, config),
      ],
      dataZoom: config.enableDataZoom
        ? [
            { type: 'inside', xAxisIndex: 0 },
            {
              type: 'slider',
              xAxisIndex: 0,
              height: Number(config.dataZoomHeight ?? 16),
              bottom: Number(config.dataZoomBottom ?? 0),
            },
          ]
        : undefined,
    };

    this.myChart.setOption(option, true);
    this.myChart.hideLoading();
    this.wrapper.afterRender();
    this.scheduleResize();
  }

  private scheduleResize(): void {
    if (this.resizeFrame) {
      cancelAnimationFrame(this.resizeFrame);
    }

    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = 0;
      this.myChart?.resize();
    });
  }

  private buildYAxis(config: any): any[] {
    const yAxes = Array.isArray(config.yAxes) && config.yAxes.length ? [...config.yAxes] : [{ name: config.yAxisName }];
    if (this.shouldShowSingleLineSeries(config) && yAxes.length < 2) {
      yAxes.push({
        name: config.lineYAxisName || config.lineSeriesName || config.lineName || '',
        position: 'right',
        splitLine: false,
        decimals: config.lineValueDecimals ?? config.lineDecimals ?? 0,
        prefix: config.lineValuePrefix ?? '',
        suffix: config.lineValueSuffix ?? '',
      });
    }

    return yAxes.map((axis: any, index: number) => ({
      type: 'value',
      name: axis.name,
      triggerEvent: true,
      position: axis.position || (index === 1 ? 'right' : 'left'),
      min: axis.min,
      max: axis.max,
      interval: axis.interval,
      splitLine: {
        show: axis.splitLine !== false && index === 0,
        lineStyle: { color: axis.splitLineColor || '#eeeeee', type: axis.splitLineType || 'solid' },
      },
      axisLabel: {
        color: axis.labelColor || config.yAxisLabelColor || '#333333',
        fontSize: Number(axis.fontSize ?? config.yAxisFontSize ?? 11),
        margin: Number(axis.labelMargin ?? config.yAxisLabelMargin ?? 8),
        formatter: (value: number) => this.formatValue(value, axis.decimals ?? 0, axis.prefix ?? '', axis.suffix ?? ''),
      },
      nameTextStyle: {
        color: axis.nameColor || config.yAxisNameColor || '#111111',
        fontSize: Number(axis.nameFontSize ?? config.yAxisNameFontSize ?? 12),
        fontWeight: axis.nameFontWeight ?? 700,
      },
    }));
  }

  private buildSeries(config: NormalizedSeriesConfig, rows: any[], categories: string[], chartConfig: any): any {
    const color = this.wrapper.applyColorConsistency(config.name, config.color) || config.color;
    const colorField = chartConfig.colorField || this.data?.colorField;
    const colorMap = chartConfig.colorMap || this.data?.colorMap || {};
    const data = categories.map((_category, index) => {
      const row = rows[index];
      const value = row ? Number(this.resolvePath(row, config.valueField)) : Number.NaN;
      const mappedColorKey = colorField && row ? this.resolvePath(row, colorField) : undefined;
      const mappedColor = mappedColorKey ? colorMap[String(mappedColorKey)] : undefined;
      return {
        value: Number.isFinite(value) ? value : null,
        raw: row,
        ...(mappedColor ? { itemStyle: { color: mappedColor } } : {}),
      };
    });

    return {
      name: config.name,
      type: config.mode,
      stack: config.mode === 'bar' && this.isStackSeriesEnabled(chartConfig) ? chartConfig.stackName || 'stack' : undefined,
      yAxisIndex: config.yAxisIndex,
      data,
      barWidth: config.mode === 'bar' ? config.barWidth ?? chartConfig.barWidth ?? 18 : undefined,
      barMaxWidth: config.mode === 'bar' ? chartConfig.barMaxWidth ?? 28 : undefined,
      smooth: config.mode === 'line' ? config.smooth ?? chartConfig.lineSmooth ?? false : undefined,
      connectNulls:
        config.mode === 'line' ? config.connectNulls ?? chartConfig.lineConnectNulls ?? true : undefined,
      symbol: config.mode === 'line' ? config.symbol ?? chartConfig.lineSymbol ?? 'circle' : undefined,
      symbolSize: config.mode === 'line' ? Number(config.symbolSize ?? chartConfig.lineSymbolSize ?? 7) : undefined,
      lineStyle:
        config.mode === 'line'
          ? { width: Number(config.lineWidth ?? chartConfig.lineWidth ?? 2), color }
          : undefined,
      itemStyle: { color },
      label: {
        show: config.showLabel ?? chartConfig.showDataLabels ?? config.mode === 'bar',
        position: config.mode === 'line' ? chartConfig.lineLabelPosition ?? 'top' : chartConfig.barLabelPosition ?? 'top',
        align: chartConfig.labelAlign ?? 'center',
        verticalAlign: chartConfig.labelVerticalAlign ?? (config.mode === 'bar' ? 'middle' : 'bottom'),
        distance: Number(chartConfig.labelDistance ?? (config.mode === 'bar' ? 10 : 6)),
        rotate: config.labelRotate ?? chartConfig.labelRotate ?? 90,
        color: chartConfig.labelColor || '#111111',
        fontSize: Number(chartConfig.labelFontSize ?? 11),
        fontWeight: chartConfig.labelFontWeight ?? 700,
        formatter: (params: any) =>
          this.formatValue(
            Number(params.value),
            config.valueDecimals ?? chartConfig.valueDecimals ?? 0,
            config.valuePrefix ?? '',
            config.valueSuffix ?? '',
          ),
      },
      emphasis: { focus: 'series' },
    };
  }

  private buildStackTotalSeries(
    seriesConfigs: NormalizedSeriesConfig[],
    rows: any[],
    categories: string[],
    chartConfig: any,
  ): any[] {
    if (!this.isStackSeriesEnabled(chartConfig) || !this.isTruthy(chartConfig.showStackTotalLabels)) {
      return [];
    }

    const barSeries = seriesConfigs.filter((series) => series.mode === 'bar');
    if (!barSeries.length) {
      return [];
    }

    const data = categories.map((_category, index) => {
      const row = rows[index];
      const total = barSeries.reduce((sum, series) => {
        const value = row ? Number(this.resolvePath(row, series.valueField)) : Number.NaN;
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);
      return Number.isFinite(total) ? total : null;
    });

    return [
      {
        name: chartConfig.stackTotalSeriesName || '__stackTotal',
        type: 'line',
        data,
        yAxisIndex: 0,
        symbol: 'none',
        smooth: false,
        silent: true,
        tooltip: { show: false },
        lineStyle: { opacity: 0 },
        itemStyle: { opacity: 0 },
        label: {
          show: true,
          position: chartConfig.stackTotalLabelPosition || 'top',
          distance: Number(chartConfig.stackTotalLabelDistance ?? 8),
          color: chartConfig.stackTotalLabelColor || '#202020',
          fontSize: Number(chartConfig.stackTotalLabelFontSize ?? 12),
          fontWeight: chartConfig.stackTotalLabelFontWeight ?? 700,
          formatter: (params: any) =>
            this.formatValue(
              Number(params.value),
              Number(chartConfig.stackTotalValueDecimals ?? chartConfig.valueDecimals ?? 0),
              chartConfig.stackTotalValuePrefix ?? '',
              chartConfig.stackTotalValueSuffix ?? '',
            ),
        },
        emphasis: { disabled: true },
      },
    ];
  }

  private resolveSeriesConfigs(rows: any[]): NormalizedSeriesConfig[] {
    const config = this.getConfig();
    const palette = this.palette();
    const configured = [
      ...(Array.isArray(config.series) ? config.series : []),
      ...(Array.isArray(config.lineSeries) ? config.lineSeries.map((item: any) => ({ ...item, mode: 'line' })) : []),
    ];
    let seriesConfigs: NormalizedSeriesConfig[] = [];

    if (configured.length) {
      seriesConfigs = configured.map((item: any, index: number) => ({
        mode: String(item.mode || item.type || 'bar').toLowerCase() === 'line' ? 'line' : 'bar',
        name: String(item.name || item.label || item.valueField || `Series ${index + 1}`),
        valueField: String(item.valueField || item.key || this.valueKey()),
        color: item.color || palette[index % palette.length],
        yAxisIndex: Number(item.yAxisIndex ?? (String(item.mode || item.type).toLowerCase() === 'line' ? 1 : 0)),
        barWidth: item.barWidth,
        lineWidth: item.lineWidth,
        smooth: item.smooth,
        connectNulls: item.connectNulls,
        symbol: item.symbol,
        symbolSize: item.symbolSize,
        showLabel: item.showLabel,
        labelRotate: item.labelRotate,
        valuePrefix: item.valuePrefix ?? item.prefix,
        valueSuffix: item.valueSuffix ?? item.suffix,
        valueDecimals: item.valueDecimals ?? item.decimals,
      }));
    } else {
      const seriesKey = config.seriesKey || this.data?.seriesKey;
      if (seriesKey) {
        seriesConfigs = Array.from(new Set(rows.map((row) => String(this.resolvePath(row, seriesKey))).filter(Boolean))).map(
        (name, index) => ({
          mode: 'bar',
          name,
          valueField: `__seriesValue.${name}`,
          color: config.colorMap?.[name] || palette[index % palette.length],
          yAxisIndex: 0,
        }),
      );
      } else {
        seriesConfigs = [
          {
            mode: 'bar',
            name: String(config.seriesName || this.data?.seriesName || 'Value'),
            valueField: this.valueKey(),
            color: palette[0],
            yAxisIndex: 0,
          },
        ];
      }
    }

    const singleLineKey = this.resolveSingleLineSeriesKey(config);
    const hasSingleLineSeries = seriesConfigs.some(
      (series) => series.mode === 'line' && series.valueField === singleLineKey,
    );
    if (this.shouldShowSingleLineSeries(config) && singleLineKey && !hasSingleLineSeries) {
      seriesConfigs.push({
        mode: 'line',
        name: config.lineSeriesName || config.lineName || singleLineKey,
        valueField: singleLineKey,
        color: config.lineColor || palette[seriesConfigs.length % palette.length],
        yAxisIndex: Number(config.lineYAxisIndex ?? 1),
        lineWidth: config.lineWidth,
        smooth: config.lineSmooth,
        connectNulls: config.lineConnectNulls,
        symbol: config.lineSymbol,
        symbolSize: config.lineSymbolSize,
        showLabel: config.showLineLabels ?? config.lineShowLabel,
        labelRotate: config.lineLabelRotate,
        valuePrefix: config.lineValuePrefix,
        valueSuffix: config.lineValueSuffix,
        valueDecimals: config.lineValueDecimals ?? config.lineDecimals,
      });
    }

    return seriesConfigs;
  }

  private shouldShowSingleLineSeries(config: any): boolean {
    return this.isTruthy(config.lineVisible ?? config.linevisible ?? this.data?.lineVisible ?? this.data?.linevisible);
  }

  private resolveSingleLineSeriesKey(config: any): string {
    const lineSeriesValue = Array.isArray(config.lineSeries) ? '' : config.lineSeries;
    const dataLineSeriesValue = Array.isArray(this.data?.lineSeries) ? '' : this.data?.lineSeries;
    return String(
      config.lineSeriesKey ||
        config.lineSeriesValueKey ||
        config.lineValueKey ||
        config.lineKey ||
        lineSeriesValue ||
        this.data?.lineSeriesKey ||
        this.data?.lineSeriesValueKey ||
        this.data?.lineValueKey ||
        this.data?.lineKey ||
        dataLineSeriesValue ||
        '',
    );
  }

  private isTruthy(value: any): boolean {
    return value === true || String(value).toLowerCase() === 'true';
  }

  private buildCategories(rows: any[]): string[] {
    return rows.map((row, index) => this.categoryLabel(row, index));
  }

  private normalizeRowsForConfiguredSeries(rows: any[]): any[] {
    const seriesKey = this.getConfig().seriesKey || this.data?.seriesKey;
    if (!seriesKey) {
      return rows;
    }

    const groupedRows = new Map<string, any>();
    rows.forEach((row, index) => {
      const category = this.categoryLabel(row, index);
      const seriesName = String(this.resolvePath(row, seriesKey) ?? '');
      const value = this.resolvePath(row, this.valueKey());
      const existing = groupedRows.get(category) || { ...row, __seriesValue: {} };
      const existingValue = Number(existing.__seriesValue?.[seriesName] ?? 0);
      const numericValue = Number(value);
      const lineSeriesKey = this.resolveSingleLineSeriesKey(this.getConfig());
      const lineValue = lineSeriesKey ? Number(this.resolvePath(row, lineSeriesKey)) : Number.NaN;
      const existingLineValue = lineSeriesKey ? Number(existing[lineSeriesKey] ?? 0) : Number.NaN;
      groupedRows.set(category, {
        ...existing,
        ...(lineSeriesKey && Number.isFinite(lineValue)
          ? { [lineSeriesKey]: (Number.isFinite(existingLineValue) ? existingLineValue : 0) + lineValue }
          : {}),
        __seriesValue: {
          ...(existing.__seriesValue || {}),
          [seriesName]: Number.isFinite(numericValue) ? existingValue + numericValue : value,
        },
      });
    });

    return Array.from(groupedRows.values());
  }

  private isStackSeriesEnabled(config: any): boolean {
    return this.isTruthy(
      config.stackSeries ??
        config.stackVisible ??
        config.stackBarVisible ??
        config.stackedBar ??
        this.data?.stackSeries ??
        this.data?.stackVisible ??
        this.data?.stackBarVisible ??
        this.data?.stackedBar,
    );
  }

  private limitRows(rows: any[]): any[] {
    const maxItems = Number(this.getConfig().maxItems ?? this.data?.maxItems ?? 0);
    if (!Number.isFinite(maxItems) || maxItems <= 0 || rows.length <= maxItems) {
      return rows;
    }

    return rows.slice(0, maxItems);
  }

  private buildDivisionRows(rows: any[], categories: string[]): DivisionRow[] {
    const configuredRows = this.getConfig().divisionRows || this.getConfig().divisions || this.data?.divisionRows || [];
    if (!Array.isArray(configuredRows)) {
      return [];
    }

    return configuredRows.map((rowConfig: any) => {
      const explicitSegments = Array.isArray(rowConfig.segments)
        ? rowConfig.segments
        : this.compressSegments(rows, categories, String(rowConfig.valueKey || rowConfig.key || ''));

      return {
        label: String(rowConfig.label || rowConfig.name || rowConfig.valueKey || ''),
        valueKey: String(rowConfig.valueKey || rowConfig.key || ''),
        segments: explicitSegments
          .map((segment: any) => this.normalizeSegment(segment, categories, String(rowConfig.valueKey || rowConfig.key || '')))
          .filter((segment: DivisionSegment | null): segment is DivisionSegment => Boolean(segment)),
      };
    });
  }

  private compressSegments(rows: any[], categories: string[], key: string): any[] {
    if (!key) {
      return [];
    }

    const categoryToRow = new Map(rows.map((row, index) => [this.categoryLabel(row, index), row]));
    const segments: any[] = [];
    let startIndex = 0;
    let currentValue = '';

    categories.forEach((category, index) => {
      const value = String(this.resolvePath(categoryToRow.get(category), key) ?? '');
      if (index === 0) {
        currentValue = value;
        return;
      }

      if (value !== currentValue) {
        segments.push({ label: currentValue, startIndex, endIndex: index - 1 });
        startIndex = index;
        currentValue = value;
      }
    });

    if (categories.length) {
      segments.push({ label: currentValue, startIndex, endIndex: categories.length - 1 });
    }

    return segments;
  }

  private normalizeSegment(segment: any, categories: string[], valueKey: string): DivisionSegment | null {
    const startIndex = this.resolveSegmentIndex(segment.startIndex ?? segment.start, categories);
    const endIndex = this.resolveSegmentIndex(segment.endIndex ?? segment.end ?? segment.startIndex ?? segment.start, categories);

    if (startIndex < 0 || endIndex < 0) {
      return null;
    }

    const start = Math.min(startIndex, endIndex);
    const end = Math.max(startIndex, endIndex);
    const span = end - start + 1;
    return {
      label: String(segment.label ?? segment.name ?? ''),
      valueKey,
      startIndex: start,
      endIndex: end,
      gridColumn: `${start + 1} / ${end + 2}`,
      compact:
        Boolean(segment.compact) ||
        this.getConfig().verticalDivisionLabels === true ||
        (this.getConfig().compactSingleDivisionLabels === true && span <= 1),
    };
  }

  onDivisionSegmentClick(row: DivisionRow, segment: DivisionSegment): void {
    const categories = this.allCategories.slice(segment.startIndex, segment.endIndex + 1);
    const selectionKey = `${row.valueKey}:${segment.startIndex}:${segment.endIndex}:${segment.label}`;

    if (!categories.length) {
      return;
    }

    if (this.selectedDivisionKey === selectionKey) {
      this.selectedDivisionKey = '';
      this.wrapper.selectedCategories = [];
      this.resetCategoryHighlight();
      this.chartFilterHandler.updateFilterSelection(
        { ...this.data, filterKey: row.valueKey || this.data?.filterKey },
        [],
      );
      return;
    }

    this.selectedDivisionKey = selectionKey;
    this.wrapper.selectedCategories = categories;
    this.highlightCategories(categories);

    const selectedRows = this.lastRows.slice(segment.startIndex, segment.endIndex + 1);
    const values = Array.from(
      new Set(
        selectedRows
          .map((item) => this.resolvePath(item, row.valueKey))
          .filter((value) => value !== undefined && value !== null && value !== ''),
      ),
    );

    this.chartFilterHandler.updateFilterSelection(
      {
        ...this.data,
        filterKey: row.valueKey || this.data?.filterKey,
      },
      values.length ? values : [segment.label],
      selectedRows[0],
    );
  }

  private resolveSegmentIndex(value: any, categories: string[]): number {
    if (Number.isInteger(value)) {
      return Number(value);
    }
    return categories.findIndex((category) => String(category) === String(value));
  }

  private extractRows(payload: any): any[] {
    const dataKey = this.data?.dataKey || this.getConfig().dataKey;
    const responsePath = this.data?.responsePath || this.getConfig().responsePath;
    const source = responsePath ? this.resolvePath(payload, responsePath) : payload;
    const candidates = [
      dataKey ? this.resolvePath(source, dataKey) : undefined,
      dataKey ? this.resolvePath(payload, dataKey) : undefined,
      dataKey ? this.resolvePath(source, `data.${dataKey}`) : undefined,
      dataKey ? this.resolvePath(source, `data.0.${dataKey}`) : undefined,
      dataKey ? this.resolvePath(source, `responseData.0.${dataKey}`) : undefined,
      dataKey ? this.resolvePath(source, `responseList.0.data.0.${dataKey}`) : undefined,
      dataKey ? this.resolvePath(source, `responseList.0.data.${dataKey}`) : undefined,
      dataKey ? this.resolvePath(source, `responseList.0.responseData.0.${dataKey}`) : undefined,
      dataKey ? this.resolvePath(source, `responseList.0.responseData.${dataKey}`) : undefined,
      this.resolvePath(source, 'data.records'),
      this.resolvePath(source, 'records'),
      this.resolvePath(source, 'responseData.0'),
      this.resolvePath(source, 'responseData'),
      this.resolvePath(source, 'responseList.0.data.0'),
      this.resolvePath(source, 'responseList.0.data'),
      this.resolvePath(source, 'responseList.0.responseData.0'),
      this.resolvePath(source, 'responseList.0.responseData'),
      this.resolvePath(source, 'data.0'),
      this.resolvePath(source, 'data'),
      source,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }

      if (candidate && typeof candidate === 'object' && dataKey && Array.isArray(candidate[dataKey])) {
        return candidate[dataKey];
      }
    }

    return [];
  }

  private activeWidgetData(): any {
    const view = this.activeView();
    return {
      ...this.data,
      ...view,
      chartConfig: this.getConfig(),
    };
  }

  private activeView(): ViewTab {
    return this.viewTabs[Math.min(this.activeViewIndex, this.viewTabs.length - 1)] || this.viewTabs[0];
  }

  private getConfig(): any {
    return this.activeConfig || {};
  }

  private refreshViewState(): void {
    const rootConfig = this.data?.chartConfig || {};
    const views = rootConfig.views || this.data?.views;
    this.viewTabs = Array.isArray(views) && views.length > 0
      ? views.map((view: any, index: number) => ({
          id: String(view.id ?? view.key ?? index),
          label: String(view.label ?? view.name ?? view.title ?? `View ${index + 1}`),
          ...view,
        }))
      : [{ id: 'default', label: String(this.data?.defaultViewLabel || 'USD') }];

    const activeView = this.viewTabs[Math.min(this.activeViewIndex, this.viewTabs.length - 1)] || {};
    const { views: _views, ...baseConfig } = rootConfig;
    this.activeConfig = {
      ...baseConfig,
      ...(activeView.chartConfig || {}),
    };
  }

  private categoryLabel(row: any, index: number): string {
    return String(this.resolvePath(row, this.categoryKey()) ?? index + 1);
  }

  private categoryKey(): string {
    return String(this.getConfig().categoryKey || this.data?.categoryKey || this.data?.xAxis || 'category');
  }

  private valueKey(): string {
    return String(this.getConfig().valueKey || this.data?.valueKey || 'value');
  }

  private palette(): string[] {
    return this.getConfig().colors || this.data?.colors || ['#00618a', '#b38a2a', '#008577', '#7f9c2f', '#4f4f4f'];
  }

  private formatTooltip(params: any): string {
    const rows = Array.isArray(params) ? params : [params];
    const title = rows[0]?.axisValueLabel || rows[0]?.name || '';
    const lines = rows
      .map((item: any) => {
        const value = Array.isArray(item.value) ? item.value[1] : item.value;
        return `<div>${item.marker}<strong>${item.seriesName}</strong>: ${this.formatValue(Number(value), 0, '', '')}</div>`;
      })
      .join('');

    return `<div style="line-height:1.75;min-width:150px"><strong>${title}</strong>${lines}</div>`;
  }

  private formatValue(value: number, decimals: number, prefix: string, suffix: string): string {
    if (!Number.isFinite(value)) {
      return '-';
    }
    return `${prefix}${value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}${suffix}`;
  }

  private showNoData(): void {
    this.myChart?.clear();
    this.myChart?.setOption({
      xAxis: { show: false, type: 'category', data: [] },
      yAxis: { show: false, type: 'value' },
      series: [],
      graphic: {
        type: 'text',
        left: 'center',
        top: 'middle',
        style: {
          text: this.data?.noDataText || 'No data available',
          fill: '#666666',
          fontSize: 14,
          fontWeight: 600,
        },
      },
    });
    this.myChart?.hideLoading();
  }

  private highlightCategories(categories: string[]): void {
    if (!this.myChart) {
      return;
    }

    const selectedCategories = new Set(categories.map((category) => String(category)));
    const option = this.myChart.getOption() as any;
    const updatedSeries = (option?.series || []).map((series: any) => ({
      ...series,
      data: Array.isArray(series.data)
        ? series.data.map((point: any, index: number) => {
            const active = selectedCategories.has(String(this.allCategories[index]));
            return {
              ...point,
              itemStyle: {
                ...(point?.itemStyle || {}),
                opacity: active ? 1 : 0.14,
              },
              label: {
                ...(point?.label || {}),
                opacity: active ? 1 : 0.2,
              },
            };
          })
        : series.data,
    }));

    this.myChart.setOption({ series: updatedSeries }, false);
  }

  private resetCategoryHighlight(): void {
    if (!this.myChart) {
      return;
    }

    const option = this.myChart.getOption() as any;
    const updatedSeries = (option?.series || []).map((series: any) => ({
      ...series,
      data: Array.isArray(series.data)
        ? series.data.map((point: any) => ({
            ...point,
            itemStyle: {
              ...(point?.itemStyle || {}),
              opacity: 1,
            },
            label: {
              ...(point?.label || {}),
              opacity: 1,
            },
          }))
        : series.data,
    }));

    this.myChart.setOption({ series: updatedSeries }, false);
  }

  private selectLegendsByIndices(indices: number[]): void {
    if (!this.myChart) {
      return;
    }

    const selectedIndices = new Set(indices);
    const selected = this.allSeriesNames.reduce<Record<string, boolean>>((acc, seriesName, index) => {
      acc[seriesName] = indices.length === 0 || selectedIndices.has(index);
      return acc;
    }, {});
    this.myChart.setOption({ legend: { selected } }, false);
  }

  private resolvePath(source: any, path: string): any {
    if (!source || !path) {
      return undefined;
    }
    return String(path)
      .split('.')
      .reduce((current: any, key: string) => current?.[key], source);
  }
}
